import { useMemo, useState } from "react";
import { AvatarPersona } from "@/components/ui/avatar-persona";
import { useFotoPerfilVista } from "@/hooks/useFotoPerfilVista";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  CalendarCheck,
  UserSearch,
  Briefcase,
  Percent,
  ArrowLeftRight,
  Menu,
  Inbox,
  Workflow,
  TrendingUp,
  Banknote,
  Activity,
  BarChart3,
  MousePointerClick,
  SlidersHorizontal,
  Users,
  LucideIcon,
  Filter,
  FileText,
  FileSignature,
  Receipt,
  ShieldCheck,
  Settings,
  Bell,
  Circle,
  PieChart,
  Repeat2,
  Sparkles,
  Building2,
} from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useAuth } from "@/contexts/AuthContext";
import { useAllowedMenus } from "@/hooks/useAllowedMenus";
import { useSubmenusPortal, segmentoPadre, etiquetaSegmento } from "@/hooks/useAltaDireccionMenu";
import { useCanReturnToAdmin } from "@/hooks/useCanReturnToAdmin";
import { PortalSidebar, PORTAL_SIDEBAR_ASIDE_CLS, PORTAL_SIDEBAR_SHEET_CLS } from "@/components/admin/PortalSidebar";
import { AltaDireccionFiltersProvider } from "@/contexts/AltaDireccionFiltersContext";
import { PortalTrackingProvider } from "@/contexts/PortalTrackingContext";
import { GlobalFilterBar } from "./GlobalFilterBar";
import { AgenteCeoDrawer } from "./AgenteCeoDrawer";

/** Home del portal: el Dashboard General. */
const RUTA_HOME = "/admin/portal-alta-direccion/dashboard";

const ROUTES_SIN_FILTER_BAR = [
  "/admin/portal-alta-direccion/dashboard",
  "/admin/portal-alta-direccion/notificaciones",
  "/admin/portal-alta-direccion/prospectos",
  "/admin/portal-alta-direccion/pipeline",
  "/admin/portal-alta-direccion/red-comercial",
  "/admin/portal-alta-direccion/tickets",
  "/admin/portal-alta-direccion/escrituracion",
  "/admin/portal-alta-direccion/cobranza",
  "/admin/portal-alta-direccion/usuarios-inactivos",
  "/admin/portal-alta-direccion/dispersion-comisiones",
  // `startsWith` cubre tambien el detalle `/reventa/:id`.
  "/admin/portal-alta-direccion/reventa",
  // Cubre también el detalle de cuenta `/condominio/:id`.
  "/admin/portal-alta-direccion/condominio",
  "/admin/portal-alta-direccion/bandeja",
  "/admin/portal-alta-direccion/ciclo-venta",
  "/admin/portal-alta-direccion/comisiones-externas",
  "/admin/portal-alta-direccion/comisiones-internas",
  "/admin/portal-alta-direccion/historico-comercial",
  "/admin/portal-alta-direccion/analisis-cobranza",
  "/admin/portal-alta-direccion/ingresos-egresos",
  "/admin/portal-alta-direccion/forecast-ingresos",
  "/admin/portal-alta-direccion/estructura-comisiones",
  "/admin/portal-alta-direccion/personal",
  "/admin/portal-alta-direccion/mediciones/portales",
  "/admin/portal-alta-direccion/mediciones/menus",
  "/admin/portal-alta-direccion/mediciones/ctas",
  "/admin/portal-alta-direccion/mediciones/embudo-ofertas",
  // Cubre también `/agente-ceo/configuracion`. El agente no usa la barra: los filtros
  // le llegan por el drawer desde la pantalla donde se abre.
  "/admin/portal-alta-direccion/agente-ceo",
];

type NavLeaf = { label: string; path: string; icon: LucideIcon };
type NavSeccion = { label: string; items: NavLeaf[]; orden: number };

const ICONO_POR_RUTA: Record<string, LucideIcon> = {
  "/admin/portal-alta-direccion/dashboard":              LayoutDashboard,
  "/admin/portal-alta-direccion/citas":                  CalendarCheck,
  "/admin/portal-alta-direccion/prospectos":             UserSearch,
  "/admin/portal-alta-direccion/pipeline":               Briefcase,
  "/admin/portal-alta-direccion/ofertas":                FileText,
  "/admin/portal-alta-direccion/cobranza":               Banknote,
  "/admin/portal-alta-direccion/contratos":              FileSignature,
  "/admin/portal-alta-direccion/facturas":               Receipt,
  "/admin/portal-alta-direccion/facturas-por-cobrar":    Receipt,
  "/admin/portal-alta-direccion/facturas-por-pagar":     Receipt,
  "/admin/portal-alta-direccion/comisiones":             Percent,
  "/admin/portal-alta-direccion/comisiones-externas":    Percent,
  "/admin/portal-alta-direccion/comisiones-internas":    Percent,
  "/admin/portal-alta-direccion/estructura-comisiones":  SlidersHorizontal,
  "/admin/portal-alta-direccion/red-comercial":          Users,
  "/admin/portal-alta-direccion/personal":               Users,
  "/admin/portal-alta-direccion/dispersion-comisiones":  PieChart,
  "/admin/portal-alta-direccion/reventa":                Repeat2,
  "/admin/portal-alta-direccion/condominio":             Building2,
  "/admin/portal-alta-direccion/reportes":               BarChart3,
  "/admin/portal-alta-direccion/auditoria":              ShieldCheck,
  "/admin/portal-alta-direccion/configuracion":          Settings,
  "/admin/portal-alta-direccion/notificaciones":         Bell,
  "/admin/portal-alta-direccion/bandeja":                Inbox,
  "/admin/portal-alta-direccion/ciclo-venta":            Workflow,
  "/admin/portal-alta-direccion/historico-comercial":    TrendingUp,
  "/admin/portal-alta-direccion/analisis-cobranza":      Banknote,
  "/admin/portal-alta-direccion/ingresos-egresos":       ArrowLeftRight,
  "/admin/portal-alta-direccion/forecast-ingresos":      TrendingUp,
  "/admin/portal-alta-direccion/mediciones/portales":    Activity,
  "/admin/portal-alta-direccion/mediciones/menus":       BarChart3,
  "/admin/portal-alta-direccion/mediciones/ctas":        MousePointerClick,
  "/admin/portal-alta-direccion/mediciones/embudo-ofertas": Filter,
  "/admin/portal-alta-direccion/agente-ceo":               Sparkles,
  "/admin/portal-alta-direccion/agente-ceo/configuracion": Settings,
};

const ICONO_POR_DEFECTO: LucideIcon = Circle;

/**
 * Sección bajo la que se agrupa cada ruta. Es presentación pura —como el ícono—:
 * `submenus` no tiene columna de grupo, así que el encabezado se decide aquí. El
 * ORDEN de las secciones y de sus elementos lo sigue mandando la base (`submenus.orden`):
 * cada sección se pinta donde cae su primer submenú.
 *
 * Una ruta sin sección declarada cae en {@link SECCION_POR_DEFECTO} en vez de perderse.
 */
const SECCION_POR_RUTA: Record<string, string> = {
  "/admin/portal-alta-direccion/dashboard":              "Resumen",
  "/admin/portal-alta-direccion/citas":                  "Comercial",
  "/admin/portal-alta-direccion/prospectos":             "Comercial",
  "/admin/portal-alta-direccion/pipeline":               "Comercial",
  "/admin/portal-alta-direccion/ofertas":                "Comercial",
  "/admin/portal-alta-direccion/red-comercial":          "Comercial",
  "/admin/portal-alta-direccion/bandeja":                "Operación",
  "/admin/portal-alta-direccion/ciclo-venta":            "Operación",
  "/admin/portal-alta-direccion/contratos":              "Operación",
  "/admin/portal-alta-direccion/cobranza":               "Operación",
  "/admin/portal-alta-direccion/comisiones":             "Operación",
  "/admin/portal-alta-direccion/comisiones-externas":    "Operación",
  "/admin/portal-alta-direccion/comisiones-internas":    "Operación",
  "/admin/portal-alta-direccion/estructura-comisiones":  "Operación",
  "/admin/portal-alta-direccion/reventa":                "Operación",
  "/admin/portal-alta-direccion/condominio":             "Operación",
  "/admin/portal-alta-direccion/personal":               "Organización",
  "/admin/portal-alta-direccion/dispersion-comisiones":  "Organización",
  "/admin/portal-alta-direccion/historico-comercial":    "Análisis",
  "/admin/portal-alta-direccion/analisis-cobranza":      "Análisis",
  "/admin/portal-alta-direccion/reportes":               "Análisis",
  "/admin/portal-alta-direccion/facturas":               "Finanzas",
  "/admin/portal-alta-direccion/facturas-por-cobrar":    "Finanzas",
  "/admin/portal-alta-direccion/facturas-por-pagar":     "Finanzas",
  "/admin/portal-alta-direccion/ingresos-egresos":       "Finanzas",
  "/admin/portal-alta-direccion/forecast-ingresos":      "Finanzas",
  "/admin/portal-alta-direccion/mediciones/portales":       "Mediciones",
  "/admin/portal-alta-direccion/mediciones/menus":          "Mediciones",
  "/admin/portal-alta-direccion/mediciones/ctas":           "Mediciones",
  "/admin/portal-alta-direccion/mediciones/embudo-ofertas": "Mediciones",
  "/admin/portal-alta-direccion/agente-ceo":               "Agente CEO",
  "/admin/portal-alta-direccion/agente-ceo/configuracion": "Agente CEO",
  "/admin/portal-alta-direccion/notificaciones":         "Sistema",
  "/admin/portal-alta-direccion/auditoria":              "Sistema",
  "/admin/portal-alta-direccion/configuracion":          "Sistema",
};

const SECCION_POR_DEFECTO = "Otros";

/** Sección de una ruta: la declarada, o la del segmento intermedio si lo tiene. */
function seccionDeRuta(ruta: string): string {
  const declarada = SECCION_POR_RUTA[ruta];
  if (declarada) return declarada;
  const segmento = segmentoPadre(ruta);
  return segmento ? etiquetaSegmento(segmento) : SECCION_POR_DEFECTO;
}

export const PortalAltaDireccionLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();
  const { isPathAllowed } = useAllowedMenus();
  const { canReturnToAdmin } = useCanReturnToAdmin();
  const [mobileOpen, setMobileOpen] = useState(false);

  const { data: submenus = [] } = useSubmenusPortal();

  // El menú es el de la base: nombre, orden y presencia. Aquí solo se le cuelga el
  // ícono, se filtra por permiso y se reparte en secciones para el encabezado.
  const navSecciones = useMemo(() => {
    const permitidos = submenus.filter((s) => isPathAllowed(s.ruta));
    const porSeccion = new Map<string, NavSeccion>();

    for (const s of permitidos) {
      const label = seccionDeRuta(s.ruta);
      let seccion = porSeccion.get(label);
      if (!seccion) {
        // La sección se ancla al orden de su primer submenú: si en la base se mueve
        // un elemento, su sección se mueve con él.
        seccion = { label, items: [], orden: s.orden };
        porSeccion.set(label, seccion);
      }
      seccion.items.push({
        label: s.nombre,
        path: s.ruta,
        icon: ICONO_POR_RUTA[s.ruta] ?? ICONO_POR_DEFECTO,
      });
    }

    return [...porSeccion.values()].sort((a, b) => a.orden - b.orden);
  }, [submenus, isPathAllowed]);

  const handleNavigate = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const currentSection = (() => {
    for (const seccion of navSecciones) {
      const item = seccion.items.find((i) => isActive(i.path));
      if (item) return item.label;
    }
    return "Alta Dirección";
  })();

  const rawName = profile?.nombre || profile?.email?.split("@")[0] || "Usuario";
  const userName = rawName.trim().split(/\s+/).slice(0, 2).join(" ");
  const userRole = profile?.rol_nombre ?? "Alta Dirección";
  const initials = userName.split(" ").filter(Boolean).slice(0, 2).map((p: string) => p.charAt(0).toUpperCase()).join("") || "U";
  const fotoVista = useFotoPerfilVista();

  const sidebar = (
    <PortalSidebar
      portal="Portal Alta Dirección"
      // La marca es el atajo al home del portal (el Dashboard General).
      onMarca={() => navigate(RUTA_HOME)}
      groups={navSecciones.map((seccion) => ({
        label: seccion.label,
        items: seccion.items.map((item) => ({
          key: item.path,
          label: item.label,
          icon: item.icon,
          active: isActive(item.path),
          onSelect: () => handleNavigate(item.path),
        })),
      }))}
      usuario={{ nombre: userName, rol: userRole, iniciales: initials }}
      onRegresar={canReturnToAdmin ? () => handleNavigate("/admin") : undefined}
      onCerrarSesion={signOut}
    />
  );

  return (
    <PortalTrackingProvider portal="alta-direccion">
      <AltaDireccionFiltersProvider>
        <div className="min-h-screen flex antialiased">
          {/* Desktop sidebar */}
          <aside className={PORTAL_SIDEBAR_ASIDE_CLS}>
            {sidebar}
          </aside>

          {/* Mobile drawer */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetContent side="left" className={PORTAL_SIDEBAR_SHEET_CLS}>
              {sidebar}
            </SheetContent>
          </Sheet>

          <div className="flex-1 lg:pl-64 min-w-0">
            {/* Mobile header */}
            <header className="flex lg:hidden flex-col sticky top-0 z-20 bg-card border-b border-border">
              <div className="flex items-center px-4 pt-3 pb-2 gap-3">
                <button
                  onClick={() => setMobileOpen(true)}
                  className="p-1.5 -ml-1 rounded-md text-foreground hover:bg-muted transition-colors"
                  aria-label="Abrir menú"
                >
                  <Menu className="h-5 w-5" />
                </button>
                <div className="min-w-0 flex-1">
                  <p className="text-[15px] font-semibold text-foreground tracking-tight truncate">{currentSection}</p>
                </div>
                <AvatarPersona nombre={userName} fotoUrl={fotoVista} size="sm" />
              </div>
            </header>

            {/* En móvil `px-8` se comía 64px de los ~390 disponibles. */}
            <main className="min-h-screen bg-background px-4 py-4 sm:px-6 lg:px-8">
              {!ROUTES_SIN_FILTER_BAR.some((r) => location.pathname.startsWith(r)) && (
                <GlobalFilterBar />
              )}
              <Outlet />
            </main>

            {/* El agente vive en todas las pantallas del portal: recibe la ruta
                y los filtros vigentes, así que se le puede preguntar "¿por qué
                subió esto?" sin repetirle de qué se está hablando. */}
            <AgenteCeoDrawer />
          </div>
        </div>
      </AltaDireccionFiltersProvider>
    </PortalTrackingProvider>
  );
};

export default PortalAltaDireccionLayout;
